import { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import { ContentstackClientContext } from './contentstack-client-context';
import { INCLUDE_FALLBACK_DEFAULT_VALUE } from '../consts/fallback';
/**
 * A custom React hook for fetching data from Contentstack.
 *
 * @param uuid - The UUID of the Contentstack content type to fetch.
 * @param languageCode - The language code for the content.
 * @param findQuery - Only memoized object. The query object for filtering the content (optional).
 * @param reference - Only memoized object. An array of reference fields to include in the query (optional).
 * @param findOne - If true, fetch a single item; otherwise, fetch multiple items (default: false).
 * @param skip - If true, skip the initial fetch (default: false).
 * @param includeFallback - Query parameter to fetch the published content from its fallback locale (default: true).
 * @param onCompleted - A callback function to handle successful data retrieval (optional).
 * @param onError - A callback function to handle errors (optional).
 * @returns {UseContentstackDatResponseProps<R, TError>} An object containing the fetched data, error, loading state, and fetch function.
 */
export const useContentstackData = ({ uuid, languageCode, findQuery, reference, findOne = false, skip = false, includeFallback = INCLUDE_FALLBACK_DEFAULT_VALUE, onCompleted, onError, }) => {
    const contentstack = useContext(ContentstackClientContext);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState();
    const language = useMemo(() => languageCode.toLocaleLowerCase(), [languageCode]);
    const memoizedFindQuery = useMemo(() => findQuery ?? {}, [findQuery]);
    const memoizedReference = useMemo(() => reference ?? [], [reference]);
    const fetchContent = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = contentstack.contentstackClient
                .getStack()
                .ContentType(uuid)
                .Query()
                .query(memoizedFindQuery)
                .includeReference(memoizedReference)
                .language(language);
            if (includeFallback) {
                response.includeFallback();
            }
            response.toJSON();
            const data = (findOne ? [await response.findOne()] : (await response.find())[0]);
            onCompleted?.(data);
            setData(data);
        }
        catch (catchError) {
            setError(catchError);
            onError?.(catchError);
        }
        finally {
            setIsLoading(false);
        }
    }, [
        uuid,
        contentstack.contentstackClient,
        language,
        memoizedFindQuery,
        memoizedReference,
        findOne,
        includeFallback,
        onCompleted,
        onError,
    ]);
    useEffect(() => {
        if (!skip) {
            fetchContent();
        }
    }, [skip, fetchContent]);
    return {
        data,
        error,
        isLoading,
        fetchContent,
    };
};
//# sourceMappingURL=use-contentstack-data.js.map